import { create } from 'zustand';
import _debounce from 'lodash/debounce';
type SortOrder = 'asc' | 'desc';
import { AdditionalInfoFilterStatuses } from '@src/utils/enums';

interface AdditionalInfoState {
  itemsPerPage: number;
  pageNumber: number;
  pageCount: number;
  totalCount: number;
  sortColumn: string | null;
  sortOrder: SortOrder;
  statusFilters: string[];
  setItemsPerPage: (itemsPerPage: number) => void;
  setPageNumber: (pageNumber: number) => void;
  setPageCount: (pageCount: number) => void;
  setTotalCount: (totalCount: number) => void;
  setSortColumn: (column: string | null) => void;
  setSortOrder: (order: SortOrder) => void;
  toggleSortOrder: () => void;
  setStatusFilters: (statuses: string[]) => void;
  resetToDefaults: () => void;
}

const defaults: AdditionalInfoState = {
  itemsPerPage: 10,
  pageNumber: 1,
  pageCount: 0,
  totalCount: 0,
  sortColumn: 'dueDate',
  sortOrder: 'asc',
  statusFilters: [AdditionalInfoFilterStatuses.Open],
  setItemsPerPage: () => {},
  setPageNumber: () => {},
  setPageCount: () => {},
  setTotalCount: () => {},
  setSortColumn: () => {},
  setSortOrder: () => {},
  toggleSortOrder: () => {},
  setStatusFilters: () => {},
  resetToDefaults: () => {},
};

const debouncedSetStatusFilters = _debounce(
  (set: (partial: Partial<AdditionalInfoState>) => void, statuses: string[]) => {
    set({ statusFilters: statuses });
  },
  500
);

export const useAdditionalInfoStore = create<AdditionalInfoState>((set, get) => ({
  ...defaults,
  setItemsPerPage: (itemsPerPage) => set({ itemsPerPage }),
  setPageNumber: (pageNumber) => set({ pageNumber }),
  setPageCount: (pageCount) => set({ pageCount }),
  setTotalCount: (totalCount) => {
    const pageCount = Math.ceil(totalCount / get().itemsPerPage);
    get().setPageCount(pageCount);
    set({ totalCount });
  },
  setSortColumn: (sortColumn) => set({ sortColumn }),
  setSortOrder: (sortOrder) => set({ sortOrder }),
  toggleSortOrder: () => set((state) => ({ sortOrder: state.sortOrder === 'asc' ? 'desc' : 'asc' })),
  setStatusFilters: (statuses) => debouncedSetStatusFilters(set, statuses),
  resetToDefaults: () => set(defaults),
}));
